/**
 * MTT 票券對賬管理頁（v2.3.0）
 *
 * 4 Tab：
 * 1. 週期 Overview — 該週使用社群活動票券總成本（給工作人員填營收數據額外費用用）
 * 2. 未用票餘額 — 玩家當前未用票券（潛在未來成本）
 * 3. 玩家票券歷史 — 搜尋玩家看 grant + consume timeline
 * 4. 異常 Alert — 該週 critical alerts
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card, CardBody, CardHeader, CardTitle,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Row, Col, Table, Input, InputGroup, InputGroupAddon, Button, Alert, Badge,
} from "reactstrap";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyLoading, { TinyBusy, TinyLoadingOverlay } from "components/tiny/TinyLoading";
const API_BASE = "/api/tiny";

// 跟其他 Tiny 頁面對齊的色系
const ACCENT = "#1f8ef1";        // tab active blue
const TAB_BORDER = "2px solid #2b3553";
const COST_HL = "#00d6b4";       // 強調 banner（同 EcoBot 交收用色）
const WARN_HL = "#fbb040";
const DANGER_HL = "#fb6340";

function fmtCurrency(n) {
  if (n == null) return "-";
  return "$" + Number(n).toLocaleString();
}

function StatusBadge({ status }) {
  if (!status) return <Badge color="secondary">未跑 Phase 1.5</Badge>;
  if (status === "ok") return <Badge color="success">對賬 OK</Badge>;
  if (status === "blocked") return <Badge color="danger">擋住產報表</Badge>;
  return <Badge color="warning">{status}</Badge>;
}

function tabLinkStyle(isActive) {
  return {
    cursor: "pointer",
    backgroundColor: isActive ? ACCENT : "transparent",
    color: isActive ? "#fff" : "#9A9A9A",
    border: "none",
    borderRadius: "5px 5px 0 0",
    fontWeight: isActive ? "bold" : "normal",
  };
}

function TinyTickets() {
  const [activeTab, setActiveTab] = useState("overview");
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [overview, setOverview] = useState(null);
  const [balance, setBalance] = useState(null);
  const [playerSearchId, setPlayerSearchId] = useState("");
  const [playerHistory, setPlayerHistory] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // Tab 4 人工核可 state
  const [selectedTickets, setSelectedTickets] = useState({}); // key -> ticket obj
  const [ackReason, setAckReason] = useState("");
  const [ackBusy, setAckBusy] = useState(false);
  const [ackMsg, setAckMsg] = useState(null);

  // 載入週清單
  useEffect(() => {
    axios.get(`${API_BASE}/tickets/weeks`).then((res) => {
      const ws = res.data?.data || [];
      setWeeks(ws);
      if (ws.length > 0 && !selectedWeek) setSelectedWeek(ws[0]);
    }).catch((e) => setError(e.response?.data?.msg || e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 載入該週 overview
  const loadOverview = () => {
    if (!selectedWeek) return;
    setLoading(true);
    axios.get(`${API_BASE}/tickets/week/${selectedWeek}`)
      .then((res) => setOverview(res.data?.data))
      .catch((e) => setError(e.response?.data?.msg || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);

  // 載入未用票餘額
  const loadBalance = () => {
    setLoading(true);
    axios.get(`${API_BASE}/tickets/balance?limit=200`)
      .then((res) => setBalance(res.data?.data))
      .catch((e) => setError(e.response?.data?.msg || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (activeTab === "balance" && !balance) loadBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const searchPlayer = () => {
    if (!playerSearchId.trim()) return;
    setLoading(true);
    axios.get(`${API_BASE}/tickets/player/${playerSearchId.trim()}`)
      .then((res) => setPlayerHistory(res.data?.data))
      .catch((e) => setError(e.response?.data?.msg || e.message))
      .finally(() => setLoading(false));
  };

  const audit = overview?.audit;
  const grants = overview?.grants_imported || {};
  const alertCount = audit?.alerts?.length || 0;

  // ---- Tab 4 人工核可：把 alert 的 unmatched 票攤平成逐票清單 ----
  const tkKey = (playerId, m) => `${playerId}|${m.mtt_name}|${m.start_time}|${m.ticket_cost}`;
  const acknowledgements = overview?.acknowledgements || [];     // 已核可（待重跑套用）
  const unionConfirmed = overview?.union_confirmed || [];         // v2.49.0 已確認為我方發
  const clubHints = overview?.club_hints || {};
  const confirmedSet = new Set(
    unionConfirmed.map((c) => `${c.player_id}|${c.mtt_name}|${c.start_time}|${c.ticket_cost}`)
  );
  const ackedApplied = audit?.acknowledged_self_issued || [];     // 已套用（重跑後）
  const pendingAckSet = new Set(
    acknowledgements.map((a) => `${a.player_id}|${a.mtt_name}|${a.start_time}|${a.ticket_cost}`)
  );
  const flatTickets = [];
  (audit?.alerts || []).forEach((a) => {
    if (!a.player_id) return; // 跳過非票券型 alert（如 members_weekly doc 不存在）
    (a.unmatched_mtts || []).forEach((m) => {
      flatTickets.push({
        player_id: a.player_id,
        mtt_name: m.mtt_name,
        start_time: m.start_time,
        ticket_cost: m.ticket_cost,
        club_id: m.mtt_club_id,
        player_club_id: m.player_club_id,
        player_club_name: m.player_club_name,
        key: tkKey(a.player_id, m),
      });
    });
  });

  // v2.49.0（Brad 2026-09-30）：9/28 前有客服用錯誤方式發票、沒進 Trade Record，玩家手上可能還有
  // 沒用掉的我方票。不是常自發的俱樂部（尤其儲值制）跳出來的票，很可能其實是我方發的 —— 醒目提醒人工核對。
  const attentionOf = (t) => (clubHints[String(t.player_club_id)] || {}).attention || null;
  const attentionTickets = flatTickets.filter((t) => attentionOf(t));
  const likelyUnionCount = attentionTickets.filter((t) => attentionOf(t) === "likely_union").length;

  // overview 載入後清空勾選：v2.49.0 起有兩個方向相反的按鈕（我方發 / 俱樂部自發），
  // 預設全選的話按錯一顆就整批判錯 —— 一律由人逐張勾
  useEffect(() => {
    setSelectedTickets({});
    setAckMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview]);

  const toggleTicket = (t) => {
    setSelectedTickets((prev) => {
      const next = { ...prev };
      if (next[t.key]) delete next[t.key];
      else next[t.key] = t;
      return next;
    });
  };

  const submitAck = () => {
    const tickets = Object.values(selectedTickets);
    if (tickets.length === 0) {
      setError("請至少勾選一張要忽略的票券");
      return;
    }
    setAckBusy(true);
    setAckMsg(null);
    axios.post(`${API_BASE}/tickets/acknowledge`, {
      week: selectedWeek,
      reason: ackReason,
      tickets,
    }).then((res) => {
      if (res.data?.code === 1) {
        setAckMsg(res.data.msg || `已核可 ${tickets.length} 張票券`);
        setAckReason("");
        loadOverview();
      } else {
        setError(res.data?.msg || "核可失敗");
      }
    }).catch((e) => setError(e.response?.data?.msg || e.message))
      .finally(() => setAckBusy(false));
  };

  // v2.49.0：查到發票人是我方帳號 → 確認是我方發的（補一張送票紀錄，重跑 1.5 就會補回輸贏）
  const submitConfirmUnion = () => {
    const tickets = Object.values(selectedTickets);
    if (tickets.length === 0) {
      setError("請至少勾選一張票券");
      return;
    }
    setAckBusy(true);
    setAckMsg(null);
    axios.post(`${API_BASE}/tickets/confirm-union-issued`, {
      week: selectedWeek,
      reason: ackReason,
      tickets,
    }).then((res) => {
      if (res.data?.code === 1) {
        setAckMsg(res.data.msg || `已確認 ${tickets.length} 張為我方發的票`);
        setAckReason("");
        loadOverview();
      } else {
        setError(res.data?.msg || "確認失敗");
      }
    }).catch((e) => setError(e.response?.data?.msg || e.message))
      .finally(() => setAckBusy(false));
  };

  const removeConfirm = (c) => {
    axios.delete(`${API_BASE}/tickets/confirm-union-issued`, { data: { grant_id: c._id } })
      .then(() => loadOverview())
      .catch((e) => setError(e.response?.data?.msg || e.message));
  };

  const removeAck = (a) => {
    axios.delete(`${API_BASE}/tickets/acknowledge`, { data: { ack_id: a._id } })
      .then(() => loadOverview())
      .catch((e) => setError(e.response?.data?.msg || e.message));
  };

  return (
    <div className="content">
      {error && (
        <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>
      )}

      {/* ⚠️ 標題原本寫「MTT 票券對賬」，側欄寫「票券對賬」—— 用側欄那個。
          週選擇器是範圍控制 → scope；四個 tab 是子視圖 → 公版。
          「異常 Alert」的數量原本用 Badge 掛在 tab 上，改用公版的 count。 */}
      <TinyPageHeader
        title="票券對賬"
        description={
          <>
            社群活動贈送票券的 lifecycle 對賬。Phase 1.5 對賬詳情見{" "}
            <a href="/admin/tiny-reports" style={{ color: ACCENT }}>報表整合中心</a>。
          </>
        }
        scope={<TinyWeekSelector
                        label=""
                        options={weeks}
                        value={selectedWeek}
                        onChange={setSelectedWeek}
                        showMeta={false}
                        inputStyle={{ backgroundColor: "#27293d", border: "1px solid #2b3553" }}
                      />}
        tabs={{
          items: [
            { key: "overview", label: "週期 Overview", icon: "fa fa-dashboard" },
            { key: "balance", label: "未用票餘額", icon: "fa fa-database" },
            { key: "player", label: "玩家票券歷史", icon: "fa fa-user" },
            { key: "alerts", label: "異常 Alert", icon: "fa fa-exclamation-triangle",
              count: alertCount > 0 ? alertCount : undefined },
          ],
          active: activeTab,
          onChange: setActiveTab,
        }}
      />

      <TabContent activeTab={activeTab}>
        {/* ============ Tab 1: Overview ============ */}
        <TabPane tabId="overview">
          {loading && !audit && (
            <TinyLoading text="載入週期資料" variant="block" />
          )}

          {!loading && !audit && (
            <Row>
              <Col md="12">
                <Alert color="warning">
                  <i className="fa fa-info-circle mr-2" />
                  該週尚未跑 Phase 1.5 對賬。請先到「報表整合中心」執行 Phase 1.5。
                </Alert>
              </Col>
            </Row>
          )}

          {audit && (
            <TinyLoadingOverlay active={loading} text="載入週期資料">
              {/* 核心成本 banner — 工作人員填營收數據額外費用 */}
              <Row>
                <Col md="12">
                  <Card style={{ backgroundColor: "rgba(0, 214, 180, 0.05)", border: `1px solid ${COST_HL}` }}>
                    <CardHeader>
                      <CardTitle tag="h4" style={{ marginBottom: 0 }}>
                        <i className="fa fa-calculator mr-2" />
                        本週使用社群活動票券總成本
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <Row className="align-items-center">
                        <Col md="8">
                          <div style={{ fontSize: "0.9em", color: "#9A9A9A", marginBottom: 4 }}>
                            玩家有花掉的「我方手動送的免費票券」面額總和
                          </div>
                          <div style={{ fontSize: "2.6em", fontWeight: 700, color: COST_HL }}>
                            {fmtCurrency(audit.total_consumed_value)}
                          </div>
                          <div style={{ marginTop: 6, color: "#fbb040", fontWeight: 600 }}>
                            <i className="fa fa-arrow-right mr-1" />
                            請手動填入「營收數據 → 額外費用」欄位
                          </div>
                        </Col>
                        <Col md="4" className="text-right">
                          <div style={{ marginBottom: 8 }}>
                            <StatusBadge status={audit.status} />
                          </div>
                          <div style={{ color: "#9A9A9A", fontSize: "0.9em" }}>
                            配對 <strong style={{ color: "#fff" }}>{audit.total_consumed_grants || 0}</strong> 張票
                          </div>
                          <div style={{ color: "#9A9A9A", fontSize: "0.9em" }}>
                            受惠 <strong style={{ color: "#fff" }}>{audit.player_compensations?.length || 0}</strong> 玩家
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row className="mt-3">
                {/* 按面額拆分 */}
                <Col md="6">
                  <Card>
                    <CardHeader>
                      <CardTitle tag="h4">
                        <i className="fa fa-list mr-2" />
                        使用票券按面額拆分
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>面額</th>
                            <th className="text-right">使用張數</th>
                            <th className="text-right">小計</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(audit.consumed_by_face_value || {})
                            .sort(([a], [b]) => Number(a) - Number(b))
                            .map(([fv, slot]) => (
                              <tr key={fv}>
                                <td>{fmtCurrency(Number(fv))}</td>
                                <td className="text-right">{slot.count}</td>
                                <td className="text-right" style={{ fontWeight: 600 }}>
                                  {fmtCurrency(slot.total)}
                                </td>
                              </tr>
                            ))}
                          <tr style={{ borderTop: `1px solid ${ACCENT}` }}>
                            <td><strong>合計</strong></td>
                            <td className="text-right">
                              <strong>{audit.total_consumed_grants || 0}</strong>
                            </td>
                            <td className="text-right">
                              <strong style={{ color: COST_HL }}>{fmtCurrency(audit.total_consumed_value)}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </Col>

                {/* 該週送票統計 */}
                <Col md="6">
                  <Card>
                    <CardHeader>
                      <CardTitle tag="h4">
                        <i className="fa fa-database mr-2" />
                        該週送票統計
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col xs="6">
                          <div style={{ color: "#9A9A9A", fontSize: 12 }}>送出張數</div>
                          <div style={{ fontSize: "1.7em", fontWeight: 600 }}>{grants.total_count || 0}</div>
                        </Col>
                        <Col xs="6">
                          <div style={{ color: "#9A9A9A", fontSize: 12 }}>送出總面額</div>
                          <div style={{ fontSize: "1.7em", fontWeight: 600 }}>{fmtCurrency(grants.total_value)}</div>
                        </Col>
                      </Row>
                      <Row className="mt-3">
                        <Col xs="6">
                          <div style={{ color: "#9A9A9A", fontSize: 12 }}>已使用</div>
                          <div style={{ fontSize: "1.4em", fontWeight: 600, color: COST_HL }}>
                            {grants.consumed_count || 0}
                          </div>
                          <small style={{ color: "#9A9A9A" }}>
                            {fmtCurrency(grants.consumed_value)}
                          </small>
                        </Col>
                        <Col xs="6">
                          <div style={{ color: "#9A9A9A", fontSize: 12 }}>未使用（潛在成本）</div>
                          <div style={{ fontSize: "1.4em", fontWeight: 600, color: WARN_HL }}>
                            {(grants.total_count || 0) - (grants.consumed_count || 0)}
                          </div>
                          <small style={{ color: "#9A9A9A" }}>
                            {fmtCurrency((grants.total_value || 0) - (grants.consumed_value || 0))}
                          </small>
                        </Col>
                      </Row>
                      {overview?.cross_week_consumes?.length > 0 && (
                        <div style={{ marginTop: 14, padding: 10, backgroundColor: "rgba(0,0,0,0.18)", borderRadius: 6 }}>
                          <div style={{ color: "#9A9A9A", fontSize: 12, marginBottom: 4 }}>
                            <i className="fa fa-calendar mr-1" />
                            該週送的票被「跨週」使用
                          </div>
                          {overview.cross_week_consumes.map((c) => (
                            <div key={c.consumed_in_week} style={{ fontSize: 13 }}>
                              {c.consumed_in_week}: {c.count} 張 ({fmtCurrency(c.value)})
                            </div>
                          ))}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </TinyLoadingOverlay>
          )}
        </TabPane>

        {/* ============ Tab 2: Balance ============ */}
        <TabPane tabId="balance">
          {!balance && loading && (
            <TinyLoading text="載入中" variant="block" />
          )}
          {balance && (
            <>
              <Row>
                <Col md="12">
                  <Card style={{ backgroundColor: "rgba(251, 176, 64, 0.06)", border: `1px solid ${WARN_HL}` }}>
                    <CardBody>
                      <Row className="align-items-center">
                        <Col md="4" className="text-center">
                          <div style={{ color: "#9A9A9A", fontSize: 12 }}>未用票玩家數</div>
                          <div style={{ fontSize: "2em", fontWeight: 700 }}>{balance.totals.unique_players}</div>
                        </Col>
                        <Col md="4" className="text-center">
                          <div style={{ color: "#9A9A9A", fontSize: 12 }}>未用票總張數</div>
                          <div style={{ fontSize: "2em", fontWeight: 700 }}>{balance.totals.total_count}</div>
                        </Col>
                        <Col md="4" className="text-center">
                          <div style={{ color: "#9A9A9A", fontSize: 12 }}>未用票總面額（潛在成本）</div>
                          <div style={{ fontSize: "2em", fontWeight: 700, color: WARN_HL }}>
                            {fmtCurrency(balance.totals.total_value)}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="12">
                  <Card>
                    <CardBody>
                      <Table responsive hover>
                        <thead>
                          <tr>
                            <th>玩家</th>
                            <th>俱樂部</th>
                            <th className="text-right">張數</th>
                            <th className="text-right">總面額</th>
                            <th>面額分布</th>
                            <th>最早送票</th>
                          </tr>
                        </thead>
                        <tbody>
                          {balance.rows.map((r) => (
                            <tr key={r.player_id}>
                              <td>
                                <div>{r.player_nickname}</div>
                                <small style={{ color: "#9A9A9A" }}>{r.player_id}</small>
                              </td>
                              <td>{r.club_name}</td>
                              <td className="text-right">{r.count}</td>
                              <td className="text-right" style={{ fontWeight: 600 }}>
                                {fmtCurrency(r.total_value)}
                              </td>
                              <td>
                                {r.by_face_value.map((b) => `${fmtCurrency(b.face_value)}×${b.count}`).join(", ")}
                              </td>
                              <td>
                                <small>{r.oldest_granted_at ? new Date(r.oldest_granted_at).toLocaleDateString() : "-"}</small>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </TabPane>

        {/* ============ Tab 3: Player History ============ */}
        <TabPane tabId="player">
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <InputGroup>
                    <Input
                      placeholder="輸入玩家 ID（譬如 6337-8034）"
                      value={playerSearchId}
                      onChange={(e) => setPlayerSearchId(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && searchPlayer()}
                    />
                    <InputGroupAddon addonType="append">
                      <Button color="primary" onClick={searchPlayer}>
                        <i className="fa fa-search mr-1" /> 查詢
                      </Button>
                    </InputGroupAddon>
                  </InputGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {playerHistory && (
            <>
              <Row className="mt-3">
                <Col md="12">
                  <Card>
                    <CardHeader>
                      <CardTitle tag="h4" style={{ marginBottom: 0 }}>
                        <i className="fa fa-user mr-2" />
                        {playerHistory.player_nickname}
                        <small style={{ color: "#9A9A9A", marginLeft: 10 }}>
                          ({playerHistory.player_id})
                        </small>
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col md="4">
                          <div style={{ color: "#9A9A9A", fontSize: 12 }}>累計收到</div>
                          <div style={{ fontSize: "1.4em", fontWeight: 600 }}>
                            {playerHistory.summary.total_received} 張
                          </div>
                          <small style={{ color: "#9A9A9A" }}>
                            {fmtCurrency(playerHistory.summary.total_received_value)}
                          </small>
                        </Col>
                        <Col md="4">
                          <div style={{ color: "#9A9A9A", fontSize: 12 }}>已使用</div>
                          <div style={{ fontSize: "1.4em", fontWeight: 600, color: COST_HL }}>
                            {playerHistory.summary.total_consumed} 張
                          </div>
                          <small style={{ color: "#9A9A9A" }}>
                            {fmtCurrency(playerHistory.summary.total_consumed_value)}
                          </small>
                        </Col>
                        <Col md="4">
                          <div style={{ color: "#9A9A9A", fontSize: 12 }}>未使用</div>
                          <div style={{ fontSize: "1.4em", fontWeight: 600, color: WARN_HL }}>
                            {playerHistory.summary.outstanding_count} 張
                          </div>
                          <small style={{ color: "#9A9A9A" }}>
                            {fmtCurrency(playerHistory.summary.outstanding_value)}
                          </small>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="12">
                  <Card>
                    <CardBody>
                      <Table responsive hover>
                        <thead>
                          <tr>
                            <th>送票時間</th>
                            <th>送票人</th>
                            <th>俱樂部</th>
                            <th className="text-right">面額</th>
                            <th>狀態</th>
                            <th>使用情形</th>
                          </tr>
                        </thead>
                        <tbody>
                          {playerHistory.grants.map((g) => (
                            <tr key={g._id}>
                              <td><small>{g.granted_at}</small></td>
                              <td>{g.granter?.nickname || "-"}</td>
                              <td>{g.receiver?.club_name || "-"}</td>
                              <td className="text-right" style={{ fontWeight: 600 }}>
                                {fmtCurrency(g.face_value)}
                              </td>
                              <td>
                                {g.consumed_at
                                  ? <Badge color="success">已用</Badge>
                                  : <Badge color="warning">未用</Badge>}
                              </td>
                              <td>
                                {g.consumed_in_mtt ? (
                                  <small>
                                    <div>{g.consumed_in_mtt.mtt_name}</div>
                                    <span style={{ color: "#9A9A9A" }}>{g.consumed_at}</span>
                                  </small>
                                ) : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </TabPane>

        {/* ============ Tab 4: Alerts / 人工核可 ============ */}
        <TabPane tabId="alerts">
          <Row>
            <Col md="12">
              {!audit && (
                <Alert color="warning"><i className="fa fa-info-circle mr-2" />該週尚未跑 Phase 1.5。</Alert>
              )}
              {audit && alertCount === 0 && flatTickets.length === 0 && (
                <Alert color="success">
                  <i className="fa fa-check-circle mr-2" />
                  該週無異常 alert（status=ok，可以產報表）
                </Alert>
              )}

              {/* 卡住的 unmatched 票券 → 逐票核可忽略 */}
              {audit && flatTickets.length > 0 && (
                <>
                  {attentionTickets.length > 0 && (
                    <div style={{
                      background: "rgba(245, 54, 92, 0.12)", border: "2px solid #f5365c", borderRadius: 8,
                      padding: "14px 18px", marginBottom: 14, color: "#fff",
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: "#ff6b81", marginBottom: 6 }}>
                        <i className="fa fa-triangle-exclamation mr-2" />
                        有 {attentionTickets.length} 張票不是常自發的俱樂部{likelyUnionCount ? `（其中 ${likelyUnionCount} 張是儲值制）` : ""}，很可能是我方發的 —— 請 Brad 與 Apex 一起核對
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.8, color: "rgba(255,255,255,0.85)" }}>
                        9/28 以前有客服用錯誤的方式發票，那些票沒進 Trade Record，玩家可能現在才用掉。
                        請到 ClubGG 後台查這些玩家的 Balance History 看發票人：是我方帳號就按「確認是我方發的」，
                        是俱樂部主 / 代理才按「確定忽略」。<strong>查不到、不確定的先不要按，找 Brad。</strong>
                        下方表格標紅 / 標橘的就是這些票。
                      </div>
                    </div>
                  )}
                  <Card style={{ backgroundColor: "rgba(251, 99, 64, 0.05)", border: `1px solid ${DANGER_HL}` }}>
                    <CardBody>
                      <div style={{ color: DANGER_HL, fontWeight: 700, marginBottom: 6 }}>
                        <i className="fa fa-exclamation-triangle mr-2" />
                        該週有 {flatTickets.length} 張票券「不在聯盟票券數據、也非免費賽得來」，產報表已被擋住
                      </div>
                      <div style={{ color: "#9A9A9A", fontSize: 13, lineHeight: 1.7 }}>
                        請到 <strong>ClubGG 管理員後台</strong>，查這位玩家的 Balance History 裡這張票是<strong>誰發的</strong>：
                        <br />・發票人是<strong>我方帳號</strong>（客服 / GM）→ 勾選後按「<strong>確認是我方發的</strong>」：會補回玩家輸贏，票券成本記進對帳總額。
                        <br />・發票人是<strong>俱樂部主 / 代理</strong> → 勾選後按「<strong>確定忽略（俱樂部自發）</strong>」：不補償，成本由俱樂部自己承擔。
                        <br />兩種做完都要到 <a href="/admin/tiny-reports" style={{ color: ACCENT }}>報表整合中心</a> <strong>重跑 Phase 1.5</strong> 才會解鎖。
                        右邊「參考」欄是這家俱樂部的制別與過去被核可為自發的張數，只是參考，一律以 ClubGG 後台為準。
                      </div>
                    </CardBody>
                  </Card>

                  <Card className="mt-3">
                    <CardBody>
                      <Table responsive hover>
                        <thead>
                          <tr>
                            <th style={{ width: 44 }}>忽略</th>
                            <th>玩家</th>
                            <th>賽事</th>
                            <th className="text-right">面額</th>
                            <th>主辦俱樂部</th>
                            <th>玩家所屬</th>
                            <th>時間</th>
                            <th>參考</th>
                          </tr>
                        </thead>
                        <tbody>
                          {flatTickets.map((t) => {
                            const alreadyPending = pendingAckSet.has(t.key);
                            const att = attentionOf(t);
                            return (
                              <tr key={t.key} style={att ? {
                                background: att === "likely_union" ? "rgba(245, 54, 92, 0.14)" : "rgba(251, 99, 64, 0.10)",
                                boxShadow: `inset 4px 0 0 ${att === "likely_union" ? "#f5365c" : "#fb6340"}`,
                              } : undefined}>
                                <td className="text-center">
                                  <Input
                                    type="checkbox"
                                    style={{ position: "static", margin: 0 }}
                                    checked={!!selectedTickets[t.key]}
                                    onChange={() => toggleTicket(t)}
                                  />
                                </td>
                                <td>
                                  {t.player_id}
                                  {alreadyPending && (
                                    <Badge color="info" style={{ marginLeft: 6 }}>已核可自發·待重跑</Badge>
                                  )}
                                  {confirmedSet.has(t.key) && (
                                    <Badge color="success" style={{ marginLeft: 6 }}>已確認我方發·待重跑</Badge>
                                  )}
                                  {att && (
                                    <div style={{ fontSize: 11, fontWeight: 700, marginTop: 2,
                                                  color: att === "likely_union" ? "#ff6b81" : "#fb6340" }}>
                                      {att === "likely_union" ? "儲值制：很可能是我方發的，請核對" : "此俱樂部少見自發：請核對"}
                                    </div>
                                  )}
                                </td>
                                <td><small>{t.mtt_name}</small></td>
                                <td className="text-right" style={{ fontWeight: 600 }}>{fmtCurrency(t.ticket_cost)}</td>
                                <td><small>{t.club_id || "-"}</small></td>
                                <td><small>{t.player_club_name || t.player_club_id || "-"}</small></td>
                                <td><small style={{ color: "#9A9A9A" }}>{t.start_time}</small></td>
                                <td>
                                  {(() => {
                                    const h = clubHints[String(t.player_club_id)] || {};
                                    return (
                                      <small style={{ color: "#9A9A9A" }}>
                                        {h.club_type === "credit" ? "信用制" : h.club_type === "deposit" ? "儲值制" : "—"}
                                        {h.self_issued_before ? `・過去自發 ${h.self_issued_before} 張` : ""}
                                        {h.union_confirmed_before ? `・過去確認我方 ${h.union_confirmed_before} 張` : ""}
                                      </small>
                                    );
                                  })()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>

                      <Row className="align-items-center mt-2">
                        <Col md="5">
                          <Input
                            type="text"
                            placeholder="原因（建議填）：例如 ClubGG 後台查到發票人是 GM_Kevin / 俱樂部主 xxx"
                            value={ackReason}
                            onChange={(e) => setAckReason(e.target.value)}
                          />
                        </Col>
                        <Col md="7" className="text-right">
                          <span style={{ color: "#9A9A9A", marginRight: 12 }}>
                            已勾選 {Object.keys(selectedTickets).length} 張
                          </span>
                          <Button color="success" onClick={submitConfirmUnion} disabled={ackBusy} style={{ marginRight: 8 }}>
                            {ackBusy ? <TinyBusy light style={{ marginRight: 4 }} /> : <i className="fa fa-ticket mr-1" />}
                            確認是我方發的
                          </Button>
                          <Button color="warning" onClick={submitAck} disabled={ackBusy}>
                            {ackBusy ? <TinyBusy light style={{ marginRight: 4 }} /> : <i className="fa fa-check mr-1" />}
                            確定忽略（俱樂部自發）
                          </Button>
                        </Col>
                      </Row>
                      {ackMsg && (
                        <Alert color="success" className="mt-3" toggle={() => setAckMsg(null)}>
                          <i className="fa fa-check-circle mr-2" />
                          {ackMsg}　—　請到{" "}
                          <a href="/admin/tiny-reports" style={{ color: "#155724", fontWeight: 700 }}>報表整合中心</a>{" "}
                          重跑 Phase 1.5 套用。
                        </Alert>
                      )}
                    </CardBody>
                  </Card>
                </>
              )}

              {/* 其他非票券型 critical alert（如 doc 不存在）保留原樣顯示 */}
              {audit && (audit.alerts || []).some((a) => !a.player_id || !(a.unmatched_mtts || []).length) && (
                <Card className="mt-3">
                  <CardHeader><CardTitle tag="h5">其他 alert</CardTitle></CardHeader>
                  <CardBody>
                    {audit.alerts
                      .filter((a) => !a.player_id || !(a.unmatched_mtts || []).length)
                      .map((a, i) => (
                        <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                          <Badge color="danger" className="mr-2">{a.severity}</Badge>
                          {a.player_id ? `${a.player_id}: ` : ""}{a.reason}
                        </div>
                      ))}
                  </CardBody>
                </Card>
              )}

              {/* v2.49.0 已確認為我方發的票 */}
              {audit && unionConfirmed.length > 0 && (
                <Card className="mt-3">
                  <CardHeader>
                    <CardTitle tag="h5" style={{ marginBottom: 0 }}>
                      <i className="fa fa-ticket mr-2" style={{ color: COST_HL }} />
                      已確認為我方發的票
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <Table responsive>
                      <thead>
                        <tr>
                          <th>玩家</th>
                          <th>賽事</th>
                          <th className="text-right">面額</th>
                          <th>狀態</th>
                          <th>確認人 / 原因</th>
                          <th style={{ width: 90 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {unionConfirmed.map((c) => (
                          <tr key={c._id}>
                            <td>{c.player_id}</td>
                            <td><small>{c.mtt_name}</small></td>
                            <td className="text-right">{fmtCurrency(c.ticket_cost)}</td>
                            <td>{c.consumed ? <Badge color="success">已補回</Badge> : <Badge color="info">待重跑套用</Badge>}</td>
                            <td>
                              <small>{c.recorded_by || "-"}</small>
                              {c.reason && <div style={{ color: "#9A9A9A", fontSize: 12 }}>{c.reason}</div>}
                            </td>
                            <td className="text-right">
                              <Button color="link" size="sm" style={{ color: DANGER_HL }} onClick={() => removeConfirm(c)}>
                                取消
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}

              {/* 已核可忽略（自發票）清單：待重跑 + 已套用 */}
              {audit && (acknowledgements.length > 0 || ackedApplied.length > 0) && (
                <Card className="mt-3">
                  <CardHeader>
                    <CardTitle tag="h5" style={{ marginBottom: 0 }}>
                      <i className="fa fa-check-circle mr-2" style={{ color: COST_HL }} />
                      已核可忽略的俱樂部自發票
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <Table responsive>
                      <thead>
                        <tr>
                          <th>玩家</th>
                          <th>賽事</th>
                          <th className="text-right">面額</th>
                          <th>狀態</th>
                          <th>核可人 / 原因</th>
                          <th style={{ width: 90 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {acknowledgements.map((a) => {
                          const applied = ackedApplied.some(
                            (x) => x.player_id === a.player_id && x.mtt_name === a.mtt_name &&
                              x.start_time === a.start_time && x.ticket_cost === a.ticket_cost
                          );
                          return (
                            <tr key={a._id}>
                              <td>{a.player_id}</td>
                              <td><small>{a.mtt_name}</small></td>
                              <td className="text-right">{fmtCurrency(a.ticket_cost)}</td>
                              <td>
                                {applied
                                  ? <Badge color="success">已套用</Badge>
                                  : <Badge color="info">待重跑套用</Badge>}
                              </td>
                              <td>
                                <small>{a.acknowledged_by || "-"}</small>
                                {a.reason && <div style={{ color: "#9A9A9A", fontSize: 12 }}>{a.reason}</div>}
                              </td>
                              <td className="text-right">
                                <Button color="link" size="sm" style={{ color: DANGER_HL }} onClick={() => removeAck(a)}>
                                  取消核可
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </TabPane>
      </TabContent>
    </div>
  );
}

export default TinyTickets;
